// lib/api-client.ts
import axios, { AxiosError } from 'axios';
import { initCsrf, getCsrfToken } from '@/utils/csrf';
import { fromAxiosError } from '@/shared/errors/app-error';
import { reportError } from '@/shared/errors/error-reporter';
import { showPermissionRequestPrompt } from '@/shared/errors/permission-request-events';

const getBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Always use a relative URL so requests go through the Vite proxy in dev
  // and through Nginx in production — keeping everything same-origin.
  return '/api/v1';
};

export const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for session cookies and CSRF
});

// A 403 can mean two very different things:
//   1. A genuine CSRF-token failure (e.g. the session outlived the cached
//      token), which we can transparently recover from by refreshing the
//      token and retrying once.
//   2. A legitimate business/permission rejection (e.g. password expired,
//      insufficient permissions). These must NOT be retried — retrying
//      re-runs the side effects of the request (a second login attempt, a
//      second "password expired" email, etc.).
// Only Django/DRF CSRF failures carry a `detail` of the form
// "CSRF Failed: ...", so we key off that to tell the two cases apart.
const isCsrfFailure = (data: unknown): boolean => {
  const detail = (data as { detail?: unknown } | null | undefined)?.detail;
  return typeof detail === 'string' && detail.toLowerCase().includes('csrf');
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _csrfRetry?: boolean };

    if (
      error.response?.status === 403 &&
      isCsrfFailure(error.response.data) &&
      !originalRequest._csrfRetry
    ) {
      originalRequest._csrfRetry = true;

      await initCsrf();
      const newToken = getCsrfToken();
      if (newToken && originalRequest.headers) {
        originalRequest.headers['X-CSRFToken'] = newToken;
      }

      return apiClient(originalRequest);
    }

    const normalized = fromAxiosError(error);
    reportError(normalized, { operation: 'api.request' });
    showPermissionRequestPrompt(normalized);
    return Promise.reject(normalized);
  }
);

export default apiClient;
